import { useEffect, useRef } from 'react';
import { Info, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { RotatingLoader } from '@/components/ui/rotating-loader';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { buildRoute } from '@/config/routes';
import { useNavigationService } from '@/services/navigate-service';
import { useIntegrationStatus } from '../hooks/useIntegrationStatus';
import { useSessionPmsAccount } from '../hooks/useSessionPmsAccount';
import { useSyncProgress } from '../hooks/useSyncProgress';
import { integrationStatusCopy } from '../vendor-settings/status/copy';
import { vendorRegistry } from '../vendor-settings/vendorRegistry';

interface PMSSyncProps {
  /** Fired right after a refresh is requested so the list re-applies its filter chain. */
  onSyncStarted: () => void;
  handleFilterChange?: (key: string, value: string) => void;
  setIsNoList?: (val: boolean) => void;
}

/**
 * Properties-list refresh widget for PMS-connected accounts. Reads the
 * account's integration status and the shared sync state from RTK; it no
 * longer fetches the credentialed record or re-tests credentials on every
 * page load (spec §2 decision 4), and it no longer owns a polling loop.
 */
export const PMSSync = ({ onSyncStarted, handleFilterChange, setIsNoList }: PMSSyncProps) => {
  const { navigateTo } = useNavigationService();
  const account = useSessionPmsAccount();
  const { status } = useIntegrationStatus(account);
  const hasIntegration = status.kind !== 'none';
  const { sync, startSync, lastSyncedLabel, completedInSession } = useSyncProgress({
    origin: 'properties',
    account,
    skip: !hasIntegration,
  });

  // Completion side effects PropertyListPage owes its list: the effect fires
  // for any session-started completion observed while THIS component is
  // mounted (a running → complete transition it watches happen), never on
  // mount with an already-complete log, and never for a rejected request that
  // snaps running back to the old complete log (that reads running → complete
  // by kind alone, but nothing completed, so `completedInSession` stays
  // false). It keys on the fact, not `completedOrigin`: a sync whose owning
  // surface unmounted mid-run has no story to tell but still changed the list.
  // The settings→Properties handoff needs no reset of its own: settings'
  // useSyncProgress clears its own completion story on unmount (React runs
  // the old tree's unmount effects before the new tree mounts), and
  // PropertyListPage mounts fresh here and refetches with default filters.
  const justCompleted = completedInSession;
  const previousJustCompleted = useRef(justCompleted);
  useEffect(() => {
    if (!previousJustCompleted.current && justCompleted) {
      setIsNoList?.(false);
      handleFilterChange?.('activeStatus', 'Active Units');
    }
    previousJustCompleted.current = justCompleted;
  }, [justCompleted, handleFilterChange, setIsNoList]);

  if (!hasIntegration) return null;

  const pmsSource = status.pmsSource;
  const vendorDisplayName = vendorRegistry[pmsSource]?.displayName ?? 'PMS';
  const isPaused = status.kind === 'disabled';

  const refresh = () => {
    startSync(pmsSource);
    onSyncStarted();
  };

  if (sync.kind === 'running') {
    return (
      <div className="flex items-center gap-2 ml-2">
        <RotatingLoader size={20} label="Refreshing properties" />
        <Typography as="p" variant="body-medium">Refresh in progress</Typography>
      </div>
    );
  }

  if (sync.kind === 'failed' && !isPaused) {
    return (
      <div className="flex items-center gap-2 ml-2">
        <Info className="h-5 w-5 text-destructive" />
        <div className="flex flex-col">
          <Typography as="p" variant="body-medium">Refresh failed</Typography>
          <Button
            variant="link"
            className="text-primary p-0 h-auto"
            onClick={() => navigateTo(buildRoute.integrationVendor(pmsSource))}
          >
            Check Connection
          </Button>
        </div>
      </div>
    );
  }

  return (
    // max-lg: button and status sit inline on their own header row
    // (desktop keeps the stacked column beside the title).
    <div className="flex flex-col items-start ml-2 max-lg:flex-row max-lg:items-center max-lg:gap-2">
      {isPaused ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="link"
                aria-disabled="true"
                className="text-muted-foreground gap-1.5 p-0 h-auto max-lg:min-h-11 cursor-not-allowed hover:no-underline"
                onClick={(event) => event.preventDefault()}
              >
                <RefreshCw className="h-4 w-4" />
                Refresh data
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[320px]">
              {integrationStatusCopy.properties.refreshTooltip(vendorDisplayName)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Button
          type="button"
          variant="link"
          // Standalone link-styled action, so it carries the 44px touch
          // floor itself — see button.tsx's link-variant comment.
          className="text-primary gap-1.5 p-0 h-auto max-lg:min-h-11"
          onClick={refresh}
        >
          <RefreshCw className="h-4 w-4" />
          Refresh data
        </Button>
      )}
      <Typography as="p" variant="small" className="text-muted-foreground">
        {isPaused ? integrationStatusCopy.properties.syncPaused : lastSyncedLabel}
      </Typography>
    </div>
  );
};
